import type { RunOptions, RunSummary } from '../core/run';
import type { KeyValue, Meta, ProgressStore } from '../net/progress';
import type { MetaBonuses } from '../core/meta/bonuses';
import type { Content } from '../data/schema';
import { loadContent } from '../data/content';
import { loadChosenClass, resolveRunClass } from './classes';
import { metaBonusesOf, newClientId, talentsOwned } from './meta';
import { hubState } from './ui/hubState';
import { hudState } from './ui/state';
import type { DailyChoice } from './ui/dailySeed';

/** Adapte un Storage web (getItem/setItem) à l'interface KeyValue attendue par le ProgressStore. */
export function webStorageKv(storage: Storage): KeyValue {
  return {
    get: (k) => { try { return storage.getItem(k); } catch { return null; } },
    set: (k, v) => { try { storage.setItem(k, v); } catch { /* stockage indisponible */ } },
  };
}

/** KeyValue en mémoire, repli quand localStorage est indisponible (navigation privée, politique du navigateur). */
export function memoryKv(): KeyValue {
  const m = new Map<string, string>();
  return { get: (k) => m.get(k) ?? null, set: (k, v) => { m.set(k, v); } };
}

/** KeyValue persistant si localStorage est utilisable (accès, écriture, lecture), sinon en mémoire. */
export function safeLocalKv(): KeyValue {
  try {
    const storage = globalThis.localStorage;
    const probe = '__rogfus_probe__';
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return webStorageKv(storage);
  } catch {
    return memoryKv();
  }
}

/** Compléments de méta d'une run (bonus, identifiant client, seed quotidienne avec son jour et sa provenance, rangs de talents), recopiés tels quels dans les options. */
export interface RunExtra { bonuses?: MetaBonuses; clientId?: string; dailySeed?: boolean; dailyDay?: string; dailySource?: 'server' | 'local'; talentsOwned?: number; }

/**
 * Options d'une nouvelle run : étage Incarnam et seed de l'horloge, sauf `extra.seed` (seed du jour) et paramètres d'URL
 * `?seed=` (prioritaire) et `?floor=` (rejouer une salle précise, captures de vérification ; `?floor=astrub` relance l'étage M1 de test).
 * `?salle=`, `?seed=` ou `?floor=astrub` font une run d'entraînement (`practice`) : jouable, mais rien n'est enregistré (ni Reflets, ni succès, ni classement).
 */
export function runOptionsFrom(search: string, now: number, classId: string = 'xelor', extra: RunExtra & { seed?: number } = {}): RunOptions {
  const q = new URLSearchParams(search);
  const seed = Number(q.get('seed'));
  const floor = q.get('floor');
  const salle = Number(q.get('salle'));
  const baseSeed = q.has('seed') && Number.isFinite(seed) ? seed : (extra.seed ?? now);
  const opts: RunOptions = { seed: baseSeed >>> 0, classId: resolveRunClass(classId), floorId: floor === 'astrub' ? 'astrub' : 'incarnam' };
  // ?salle=1..6 : commencer à cette salle (6 = boss), pour vérifier une salle précise
  if (Number.isInteger(salle) && salle >= 2 && salle <= 6) opts.startRoom = salle - 1;
  if (q.has('salle') || q.has('seed') || floor === 'astrub') opts.practice = true;
  if (extra.bonuses !== undefined) opts.bonuses = extra.bonuses;
  if (extra.clientId !== undefined) opts.clientId = extra.clientId;
  if (extra.dailySeed !== undefined) opts.dailySeed = extra.dailySeed;
  if (extra.dailyDay !== undefined) opts.dailyDay = extra.dailyDay;
  if (extra.dailySource !== undefined) opts.dailySource = extra.dailySource;
  if (extra.talentsOwned !== undefined) opts.talentsOwned = extra.talentsOwned;
  return opts;
}

/** Options d'une run lancée depuis le hub : bonus de la méta (talents de la classe jouée), identifiant client neuf et, pour la run du jour, la seed quotidienne avec son jour et sa provenance. */
export function startRunOptions(meta: Meta, content: Content, classId: string, daily: DailyChoice | null): RunOptions {
  const cls = resolveRunClass(classId);
  const extra: RunExtra & { seed?: number } = { bonuses: metaBonusesOf(content, meta, cls), clientId: newClientId(), talentsOwned: talentsOwned(meta, content, cls), dailySeed: !!daily };
  if (daily) { extra.seed = daily.seed; extra.dailyDay = daily.day; extra.dailySource = daily.source; }
  return runOptionsFrom(globalThis.location?.search ?? '', Date.now(), cls, extra);
}

/** Options d'une nouvelle run avec les bonus de la méta courante du hub : la classe choisie dans le menu (ou mémorisée dans `rogfus.classId`) ; une classe non jouable retombe sur le Xélor. */
export const newRunOptions = (classId?: string): RunOptions => startRunOptions(hubState.meta.value, loadContent(), classId ?? loadChosenClass(safeLocalKv()), null);

/** Enregistre la run, met à jour le hub et annonce les succès gagnés (liste dans le hub, toasts au HUD). Ne lève jamais : la méta du store est posée dans tous les cas. */
export async function onRunEnded(store: ProgressStore, summary: RunSummary, hub: { meta: { value: Meta }; lastRun: { value: RunSummary | null }; newAchievements: { value: string[] } }): Promise<void> {
  hub.lastRun.value = summary;
  try {
    const result = await store.saveRun(summary);
    hub.newAchievements.value = result.newAchievements;
    hudState.pushAchievements(result.newAchievements, loadContent());
  } catch (e) {
    console.warn('enregistrement de la run impossible', e);
  }
  hub.meta.value = store.current();
}
